<template>
    <button
        v-bind="$attrs"
        :disabled="disabled"
        @click="handleClick"
        :class="{
            'base-button': true,
            solid: variant === 'solid',
            outline: variant === 'outline',
            primary: color === 'primary',
            secondary: color === 'secondary',
            warning: color === 'warning',
            xlarge: size === 'xlarge',
            large: size === 'large',
            medium: size === 'medium',
            small: size === 'small',
            xsmall: size === 'xsmall',
            disabled,
        }"
    >
        <div :class="[{'left-icon': hasLeftIcon}]">
            <slot name="left-icon"></slot>
        </div>
        <slot></slot>
        <div :class="{'right-icon': hasRightIcon}">
            <slot name="right-icon"></slot>
        </div>
    </button>
</template>
<script setup lang="ts">
import {PropType, computed, useSlots} from 'vue';

// props, context, emits, libs
const props = defineProps({
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    variant: {
        type: String as PropType<'solid' | 'outline'>,
        required: false,
        default: 'solid',
    },
    color: {
        type: String as PropType<'primary' | 'secondary' | 'warning'>,
        required: false,
        default: 'primary',
    },
    size: {
        type: String as PropType<
            'xlarge' | 'large' | 'medium' | 'small' | 'xsmall'
        >,
        required: false,
        default: 'medium',
    },
});
const slots = useSlots();
const hasLeftIcon = computed(() => {
    const fn = slots['left-icon'];
    return !!(fn && fn().length > 0);
});
const hasRightIcon = computed(() => {
    const fn = slots['right-icon'];
    return !!(fn && fn().length > 0);
});

const emit = defineEmits(['click']);

const handleClick = (e: MouseEvent) => {
    if (!props.disabled) {
        emit('click', e);
    }
};
</script>
<style lang="scss" scoped>
.base-button {
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none;
    min-width: fit-content;
    font-weight: 600;
    border-radius: 6px;
    opacity: 1;
    transition: opacity 0.35s ease;
    white-space: nowrap;
    // gap: 4px;

    .left-icon,
    .right-icon {
        position: relative;
        display: block;
        width: 16px;
        height: 16px;

        :deep() {
            svg {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            }
        }
    }
    .left-icon {
        margin-right: 4px;
        margin-left: -4px;
    }
    .right-icon {
        margin-left: 4px;
    }
    &.xlarge {
        padding: 16.5px 20px;
        font-size: 18px;
        line-height: 22px;
        border-radius: 10px;
    }
    &.large {
        padding: 14px 20px;
        font-size: 16px;
        line-height: 20px;
        border-radius: 10px;
    }
    &.medium {
        padding: 12px 16px;
        font-size: 14px;
        line-height: 16px;
        border-radius: 8px;
        height: 40px;
    }
    &.small {
        padding: 10px 12px;
        font-size: 14px;
        line-height: 16px;
        border-radius: 8px;
    }
    &.xsmall {
        padding: 9px 12px;
        font-size: 12px;
        line-height: 12px;
        border-radius: 8px;
    }
}

.disabled {
    background-color: $btn-disabled !important;
    color: $text-disabled !important;
    cursor: not-allowed;

    &.outline {
        border: 1px solid $ele-outline-btn-input !important;
        background-color: $btn-disabled !important;
        cursor: not-allowed;
    }

    .icon {
        border-left: 1px solid $black_08;

        &:deep(img) {
            filter: opacity(0.5) drop-shadow(0 0 0 $black_80) !important;
        }
    }
}

.solid {
    &.primary {
        background-color: $btn-primary;
        color: $white;
        &:hover:not([disabled]) {
            background-color: $btn-primary-hover;
        }
    }

    &.secondary {
        background-color: $btn-secondary;
        color: $NDS20;
        &:hover:not([disabled]) {
            background-color: $btn-secondary-hover;
        }
    }

    &.warning {
        background-color: $btn-warning;
        color: $text-critical-01;
        &:hover:not([disabled]) {
            background-color: $btn-warning-hover;
        }
    }
}

.outline {
    &.primary {
        border: 1px solid $ele-outline-btn;
        background-color: $btn-tertiary;
        color: $text-interactive;
        &:hover:not([disabled]) {
            background-color: $btn-tertiary-hover;
        }
    }
    &.secondary {
        border: 1px solid $NDS92;
        background-color: $btn-default;
        color: $NDS20;
        &:hover:not([disabled]) {
            background-color: $btn-default-hover;
        }
    }
}
</style>
